import { css } from "@compiled/react";
import { FormattedMessage, useIntl } from "react-intl";

import { media } from "@atlaskit/primitives/responsive";
import UFOSegment from "@atlaskit/react-ufo/segment";
import { token } from "@atlaskit/tokens";

import { Link } from "@/libraries/design/components/link/src/link";
import { wacToken } from "@/libraries/design/foundations";
import { DEFAULT_VALUES } from "@/libraries/web-framework";
import { checkFeatureFlagGate, featureFlags } from "@/utils/feature-flags";

import {
    CollectionStrategy,
    CollectionTeamwork
} from "./collections/collection-types";
import { GlobalNavProductRecommendationsSimple } from "./global-nav-product-recommendations/components/global-nav-product-recommendations-simplified";
import {
    ProductGrid,
    ProductItem,
    useProductData,
    Product,
    ProductCategory
} from "./product-subnav";

export const ProductFeaturedCollectionSubnav = ({
    isMobile
}: {
    isMobile?: boolean;
}) => {
    const intl = useIntl();
    const { productsByTag } = useProductData();

    const CollectionsContainerHeaderText = (
        <FormattedMessage
            id="GlobalNav.collections.Title"
            defaultMessage="Atlassian Collections <span>new</span>"
            values={DEFAULT_VALUES}
        />
    );

    const CollectionsMobileLabel = intl.formatMessage({
        id: "GlobalNav.collections.cloudplatform.mobilelabel",
        defaultMessage: "Featured"
    });

    const CollectionsContaineTagline = intl.formatMessage({
        id: "GlobalNav.collections.tagline",
        defaultMessage:
            "A curated set of apps designed to solve your specific challenges"
    });

    const CloudPlatformHeading = intl.formatMessage({
        id: "GlobalNav.collections.cloudplatform.heading",
        defaultMessage: "Atlassian Cloud Platform"
    });

    const CloudPlatformTagline = intl.formatMessage({
        id: "GlobalNav.collections.cloudplatform.tagline",
        defaultMessage: "The connected foundation of your system of work"
    });

    const gwp7850GlobalNavChangesForTeam25Eu = checkFeatureFlagGate(
        featureFlags.gwp7850GlobalNavChangesForTeam25Eu
    );

    if (gwp7850GlobalNavChangesForTeam25Eu) {
        return (
            <UFOSegment name="collections-container-with-rovo">
                <ProductGrid isRovoTitle={true}>
                    <div style={{ gridColumn: "span 2" }}>
                        <GlobalNavProductRecommendationsSimple
                            isMobile={isMobile}
                        />
                        <h2 className="collections-four" css={CollectionsTitle}>
                            {intl.formatMessage({
                                id: "GlobalNav.collections.featured.atlassiancollections.title",
                                defaultMessage: "Atlassian Collections"
                            })}
                        </h2>
                    </div>

                    {productsByTag[ProductCategory.Featured].map(
                        (product: Product) => (
                            <ProductItem key={product.id} {...product} />
                        )
                    )}
                </ProductGrid>
            </UFOSegment>
        );
    }

    return (
        <UFOSegment name="collections-container">
            <div css={CollectionsContainerStyle}>
                <div css={CollectionsMobileLabelCSS} className="mobile-label">
                    {CollectionsMobileLabel}
                </div>
                <h5 css={CollectionH5}>{CollectionsContainerHeaderText}</h5>
                <p css={CollectionsP} className="collections-tagline">
                    {CollectionsContaineTagline}
                </p>
                <div css={CollectionsGrid}>
                    <CollectionTeamwork />
                    <CollectionStrategy />
                </div>
                <div css={CloudPlatformSection}>
                    <h6 css={CloudPlatformH6} className="cloud-platform">
                        {CloudPlatformHeading}
                    </h6>
                    <p css={CloudPlatformP} className="cloud-platform">
                        {CloudPlatformTagline}
                    </p>
                    <Link
                        href="/platform"
                        text={intl.formatMessage({
                            id: "GlobalNav.collections.products.atlassiancloudplatform.cta",
                            defaultMessage: "Learn more"
                        })}
                        aria-label={intl.formatMessage({
                            id: "GlobalNav.collections.products.atlassiancloudplatform.cta",
                            defaultMessage: "Learn more"
                        })}
                    />
                </div>

                <div css={BottomProductsSection}>
                    <GlobalNavProductRecommendationsSimple
                        isMobile={isMobile}
                    />
                </div>
            </div>
        </UFOSegment>
    );
};

const CollectionsContainerStyle = css({
    padding: `${token("space.500")} ${token("space.500")} ${token(
        "space.200"
    )} ${token("space.400")}`,
    position: "relative",
    [media.below.md]: {
        padding: 0
    },
    [media.only.sm]: {
        width: "330px"
    }
});

const CollectionsMobileLabelCSS = css({
    display: "none",
    fontFamily: wacToken("font.charlie.display"),
    fontSize: "0.75rem",
    textTransform: "uppercase",
    margin: `${token("space.300")} 0 ${token("space.200")}`,
    [media.below.md]: {
        display: "block"
    }
});

const CollectionH5 = css({
    margin: 0,
    [media.below.md]: {
        fontSize: "1.125rem"
    },
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    span: {
        display: "inline-block",
        borderRadius: wacToken("corner.radius.pill"),
        fontSize: "0.625rem",
        padding: `${token("space.050")} ${token("space.075")}`,
        fontWeight: 600,
        lineHeight: 1,
        marginInlineStart: token("space.050"),
        color: wacToken("color.secondary.navy"),
        background: wacToken("color.extended.blue200"),
        textTransform: "uppercase",
        verticalAlign: "middle"
    }
});

const CollectionsP = css({
    [`&.collections-tagline`]: {
        fontSize: "0.875rem",
        margin: `0 0 ${token("space.300")}`
    }
});

const CollectionsTitle = css({
    [`&.collections-four`]: {
        fontSize: "0.75rem",
        margin: 0,
        textTransform: "uppercase",
        color: wacToken("color.primary.black"),
        fontWeight: 400,
        fontFamily:
            "'Charlie Text', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Noto Sans', 'Ubuntu', 'Droid Sans', 'Helvetica Neue', sans-serif",
        lineHeight: "20px"
    }
});

const CollectionsGrid = css({
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: token("space.300"),
    padding: `0 0 ${token("space.300")}`,
    [media.below.md]: {
        gridTemplateColumns: "1fr"
    }
});

const CloudPlatformSection = css({
    borderStyle: "solid",
    borderWidth: "1px 0",
    padding: `${token("space.300")} 0`,
    borderColor: wacToken("color.neutral.light300"),
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    [`a`]: {
        fontSize: "0.875rem",
        fontFamily: wacToken("font.charlie.text")
    }
});

const CloudPlatformH6 = css({
    [`&.cloud-platform`]: {
        lineHeight: 1,
        margin: 0
    },
    [media.below.md]: {
        fontSize: "1rem"
    }
});

const CloudPlatformP = css({
    [`&.cloud-platform`]: {
        fontSize: "0.875rem",
        color: wacToken("color.neutral.light800"),
        margin: `${token("space.075")} 0`
    }
});

const BottomProductsSection = css({
    padding: `${token("space.300")} 0 0`,
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    [`.base-products-grid-title`]: {
        textTransform: "uppercase",
        fontSize: "0.75rem",
        marginBlockEnd: token("space.100")
    }
});
